import React, { useEffect, useState } from "react";
import { ApplicationConfigModal, ApplicationConfigs } from "common/types";
import { Button, Dropdown, Form, Label } from "semantic-ui-react";
import { FormattedMessage } from "react-intl";
import moment from "moment";
import {
  INITIAL_CONFIG_STATE,
  MIME_TYPES,
  SPECIAL_CHARACTERS,
} from "common/constants";
import ConfigInfoState from "components/ConfigInfoState";
import Tooltip from "components/Tooltip";
import styles from "./styles.module.scss";
import messages from "./messages";

interface Props {
  config: ApplicationConfigModal | null;
  create: ApplicationConfigs;
  disabled: boolean;
  update: boolean;
  handleSubmit: (app: any) => void;
}

const initialConfigState = INITIAL_CONFIG_STATE;

const ConfigForm = ({
  handleSubmit,
  config,
  create,
  disabled,
  update,
}: Props) => {
  const initialUpdateConfig = {
    accepted_mime_types: config?.config.accepted_mime_types,
    pubsub_topic: config?.config.pubsub_topic,
    max_file_size: config?.config.max_file_size,
    read_permission: config?.config.read_permission,
    write_permission: config?.config.write_permission,
    bucket_name: config?.config.bucket_name,
  };
  const initialUpdateAppConfig: ApplicationConfigModal = {
    id: config?.id,
    config: initialUpdateConfig,
    config_version: config?.config_version,
    application_name: config?.application_name,
    deleted: config?.deleted,
    deleted_at: config?.deleted_at,
    created_at: config?.created_at,
    updated_at: config?.updated_at,
    updated_by: config?.updated_by,
    status: config?.status,
  };

  const [createAppConfig, setCreateAppConfig] = useState(create);
  const [updateAppConfig, setUpdateAppConfig] = useState(
    initialUpdateAppConfig
  );
  const [updateConfig, setUpdateConfig] = useState(initialUpdateConfig);
  const [configObj, setConfigObj] = useState(initialConfigState);
  const [submitted, setSubmitted] = useState(false);
  const [hasInvalidAppName, setHasInvalidAppName] = useState(false);
  const [formDisabled, setFormDisabled] = useState(false);

  useEffect(() => {
    setCreateAppConfig({ ...createAppConfig, config: configObj });
    setUpdateAppConfig({ ...updateAppConfig, config: updateConfig });
  }, [createAppConfig.config, updateConfig, configObj.accepted_mime_types]);

  useEffect(() => {
    if (SPECIAL_CHARACTERS.test(createAppConfig.application_name)) {
      setHasInvalidAppName(true);
    } else {
      setHasInvalidAppName(false);
    }
  }, [createAppConfig.application_name]);

  const checkRequiredConfig = (appConfig: any) => {
    return (
      appConfig.config.write_permission !== "" &&
      appConfig.application_name !== "" &&
      !hasInvalidAppName
    );
  };

  function addConfig(app: any) {
    if (update) {
      setUpdateAppConfig({ ...updateAppConfig, config: updateConfig });
      if (checkRequiredConfig(updateAppConfig)) {
        handleSubmit(updateAppConfig);
        setSubmitted(true);
      }
    } else if (checkRequiredConfig(createAppConfig)) {
      handleSubmit(app);
      setSubmitted(true);
    }
  }

  function addMimeType(mimes: any) {
    setConfigObj({
      ...configObj,
      accepted_mime_types: mimes.toString(),
    });
    if (update) {
      setUpdateConfig({
        ...updateConfig,
        accepted_mime_types: mimes.toString(),
      });
    } else {
      setCreateAppConfig({
        ...createAppConfig,
        config: configObj,
      });
    }
  }

  useEffect(() => {
    if (
      (!update && (disabled || submitted)) ||
      (update && disabled && submitted)
    ) {
      setFormDisabled(true);
    } else {
      setFormDisabled(false);
    }
  }, [update, disabled, submitted]);

  function getDate(t: any) {
    const date = new Date(t);

    return `${date.getDate()}/${
      date.getMonth() + 1
    }/${date.getFullYear()} ${date.getHours()}:${date.getMinutes()}:${date.getSeconds()}`;
  }

  return (
    <>
      <ConfigInfoState />
      <Form className={styles.container} id="config-form">
        <div className={styles.config}>
          <Form.Field required>
            <label className={styles.label}>
              <FormattedMessage {...messages.appName} />
              <Tooltip message={<FormattedMessage {...messages.infoName} />} />
            </label>
            <input
              maxLength={250}
              id="application-name"
              disabled={disabled || submitted}
              required
              onChange={(e) => {
                setCreateAppConfig({
                  ...createAppConfig,
                  application_name: e.target.value,
                });
              }}
              defaultValue={
                disabled
                  ? config?.application_name
                  : createAppConfig.application_name
              }
            />
          </Form.Field>
          {hasInvalidAppName && (
            <Label basic color="red" pointing id="invalid-name">
              Invalid application name.
            </Label>
          )}
          {disabled && (
            <>
              <Form.Field>
                <label className={styles.label}>
                  <FormattedMessage {...messages.configVersion} />
                </label>
                <input
                  id="config-version"
                  disabled={disabled || submitted}
                  defaultValue={
                    disabled
                      ? config?.config_version
                      : createAppConfig.config_version
                  }
                />
              </Form.Field>
              <Form.Field>
                <label className={styles.label}>
                  <FormattedMessage {...messages.createdAt} />
                </label>
                <Form.Input
                  disabled={disabled || submitted}
                  type="date"
                  defaultValue={moment().format("YYYY-MM-DD")}
                />
              </Form.Field>
              <Form.Field>
                <label className={styles.label}>
                  <FormattedMessage {...messages.updatedAt} />
                </label>
                <input
                  disabled={disabled || submitted}
                  defaultValue={getDate(config?.updated_at)}
                />
              </Form.Field>
              <Form.Field>
                <label className={styles.label}>
                  <FormattedMessage {...messages.deleted} />
                </label>
                <input
                  disabled={disabled || submitted}
                  defaultValue={`${config?.deleted}`}
                />
              </Form.Field>
              <Form.Field>
                <label className={styles.label}>
                  <FormattedMessage {...messages.deletedAt} />
                </label>
                <input
                  disabled={disabled || submitted}
                  defaultValue={getDate(config?.deleted_at)}
                />
              </Form.Field>
            </>
          )}
        </div>
        <div className={styles.config}>
          <Form.Field>
            <label className={styles.label}>
              <FormattedMessage {...messages.configPubsubTopic} />
              <Tooltip
                message={<FormattedMessage {...messages.infoPubSub} />}
              />
            </label>
            <input
              id="pubsub-topic"
              maxLength={250}
              disabled={formDisabled}
              onChange={(e) => {
                setConfigObj({
                  ...configObj,
                  pubsub_topic: e.target.value,
                });
                if (update) {
                  setUpdateConfig({
                    ...updateConfig,
                    pubsub_topic: e.target.value,
                  });
                } else {
                  setCreateAppConfig({
                    ...createAppConfig,
                    config: configObj,
                  });
                }
              }}
              defaultValue={config?.config.pubsub_topic}
            />
          </Form.Field>
          <Form.Field>
            <label className={styles.label}>
              <FormattedMessage {...messages.configBucketName} />
              <Tooltip
                message={<FormattedMessage {...messages.infoBucket} />}
              />
            </label>
            <input
              id="bucket-name"
              maxLength={250}
              disabled={formDisabled}
              onChange={(e) => {
                setConfigObj({
                  ...configObj,
                  bucket_name: e.target.value,
                });
                if (update) {
                  setUpdateConfig({
                    ...updateConfig,
                    bucket_name: e.target.value,
                  });
                } else {
                  setCreateAppConfig({
                    ...createAppConfig,
                    config: configObj,
                  });
                }
              }}
              defaultValue={config?.config.bucket_name}
            />
          </Form.Field>
          <Form.Field>
            <label className={styles.label}>
              <FormattedMessage {...messages.configAcceptedMimeTypes} />
              <Tooltip message={<FormattedMessage {...messages.infoMime} />} />
            </label>
            <Dropdown
              disabled={formDisabled}
              id="mime-types"
              placeholder={
                update
                  ? `${config?.config.accepted_mime_types}`
                  : "Add Mime Types"
              }
              fluid
              multiple
              selection
              options={MIME_TYPES}
              onChange={(e, data) => {
                addMimeType(data.value);
              }}
            />
          </Form.Field>
          <Form.Field>
            <label className={styles.label}>
              <FormattedMessage {...messages.configMaxFileSize} />
              <Tooltip
                message={<FormattedMessage {...messages.infoFileSize} />}
              />
            </label>
            <input
              id="max-file-size"
              maxLength={250}
              disabled={formDisabled}
              onChange={(e) => {
                setConfigObj({
                  ...configObj,
                  max_file_size: e.target.value,
                });
                if (update) {
                  setUpdateConfig({
                    ...updateConfig,
                    max_file_size: e.target.value,
                  });
                } else {
                  setCreateAppConfig({
                    ...createAppConfig,
                    config: configObj,
                  });
                }
              }}
              defaultValue={
                disabled
                  ? config?.config.max_file_size
                  : createAppConfig.config.max_file_size
              }
            />
          </Form.Field>
          <Form.Field>
            <label className={styles.label}>
              <FormattedMessage {...messages.configReadPermission} />
              <Tooltip message={<FormattedMessage {...messages.infoRead} />} />
            </label>
            <input
              id="read-permission"
              maxLength={250}
              disabled={formDisabled}
              onChange={(e) => {
                setConfigObj({
                  ...configObj,
                  read_permission: e.target.value,
                });
                if (update) {
                  setUpdateConfig({
                    ...updateConfig,
                    read_permission: e.target.value,
                  });
                } else {
                  setCreateAppConfig({
                    ...createAppConfig,
                    config: { ...configObj, read_permission: e.target.value },
                  });
                }
              }}
              defaultValue={
                disabled
                  ? config?.config.read_permission
                  : createAppConfig.config.read_permission
              }
            />
          </Form.Field>
          <Form.Field required>
            <label className={styles.label}>
              <FormattedMessage {...messages.configWritePermission} />
              <Tooltip message={<FormattedMessage {...messages.infoWrite} />} />
            </label>
            <input
              id="write-permission"
              maxLength={250}
              disabled={formDisabled}
              required
              onChange={(e) => {
                setConfigObj({
                  ...configObj,
                  write_permission: e.target.value,
                });
                if (update) {
                  setUpdateConfig({
                    ...updateConfig,
                    write_permission: e.target.value,
                  });
                } else {
                  setCreateAppConfig({
                    ...createAppConfig,
                    config: { ...configObj, write_permission: e.target.value },
                  });
                }
              }}
              defaultValue={
                disabled
                  ? config?.config.write_permission
                  : createAppConfig.config.write_permission
              }
            />
          </Form.Field>
          {(!disabled || update) && (
            <Button
              id="form-add-btn"
              disabled={submitted}
              positive
              onClick={() =>
                addConfig(update ? updateAppConfig : createAppConfig)
              }
            >
              Save
            </Button>
          )}
        </div>
      </Form>
    </>
  );
};

export default ConfigForm;
